import type { Patient, Reference } from '@medplum/fhirtypes'
import { featureFlags } from '@seen/core/featureFlags'
import { ExtendedObservation } from '@seen/core/models/observation'
import {
  frozenPanelTotal,
  latestValueKey,
} from '@seen/core/observation-logic/documentUnits'
import { instrumentGroupQuery } from '@seen/core/observation-logic/instruments/records'
import {
  captureValue,
  latestCaptureMembers,
  latestCaptureQuery,
} from '@seen/core/observation-logic/latestCapture'
import { observationValueOf } from '@seen/core/observation-logic/value'
import { OBSERVATION_MODE_LATEST } from '@seen/core/observationReferences'
import { resolveId } from '@seen/core/references'
import { useQueries } from '@tanstack/react-query'
import type { Node as PMNode } from '@tiptap/pm/model'
import { type Editor, useEditorState } from '@tiptap/react'
import { isEqual } from 'lodash-es'
import { useEffect } from 'react'
import {
  type ConditionValue,
  conditionValueKey,
  groupValueKey,
  OBSERVATION_NODE,
  OBSERVATION_PANEL_NODE,
} from '#src/components/rich-text-editor/conditional-display/conditionalDisplayState.ts'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useMedplumOptional } from '#src/hooks/useMedplum.ts'
import { useMemoValue } from '#src/hooks/useMemoValue.ts'

interface ValueTargets {
  references: string[]
  latest: { system: string; code: string }[]
  // Instrument captures in the note, by group id.
  groups: string[]
}

const NO_TARGETS: ValueTargets = { references: [], latest: [], groups: [] }

// A read still running, or failed: its value is not known yet.
function unread(query: {
  isError: boolean
  isPending: boolean
  fetchStatus: string
}): boolean {
  return query.isError || (query.isPending && query.fetchStatus !== 'idle')
}

// Feeds the conditional-display plugin the recorded value of every
// observation node in a note with any condition: its own record, read through
// the same cache entries `useRead` fills, or for a node frozen to the latest
// value, the participant's latest record for its code.
export function useObservationValueMap(
  editor: Editor | null,
  participant: Reference<Patient> | null | undefined
): void {
  const enabled = useFeatureFlag(featureFlags.structuredCapture)
  // Absent outside a Medplum provider (a bare editor): nothing to read.
  const medplum = useMedplumOptional()
  const targets =
    useEditorState({
      editor,
      selector: ({ editor: current }) =>
        enabled && current ? valueTargets(current.state.doc) : NO_TARGETS,
      equalityFn: isEqual,
    }) ?? NO_TARGETS
  const patient = participant?.reference

  const reads = useQueries({
    queries: targets.references.map((reference) => {
      const id = resolveId({ reference }) ?? ''
      return {
        queryKey: cacheKeys.readExtended(
          'Observation',
          id,
          ExtendedObservation.name
        ),
        queryFn: () =>
          medplum?.readResourceClass(ExtendedObservation, id) ?? null,
        enabled: !!id && !!medplum,
      }
    }),
  })
  const latest = useQueries({
    queries: targets.latest.map(({ system, code }) => ({
      // Under the Observation key space, so an Observation write refreshes
      // it. The client is one per session, not part of what is read.
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey: ['Observation', 'latestCapture', patient, system, code],
      // The latest capture: one record, or every record of a multi-select
      // answer.
      queryFn: async (): Promise<ExtendedObservation[]> =>
        medplum && patient
          ? latestCaptureMembers(
              await medplum.searchResourceClass(
                ExtendedObservation,
                latestCaptureQuery({ subject: patient, system, code })
              )
            )
          : [],
      enabled: !!patient && !!medplum,
    })),
  })

  const groups = useQueries({
    queries: targets.groups.map((groupId) => ({
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey: ['Observation', 'instrumentGroup', patient, groupId],
      queryFn: async (): Promise<ExtendedObservation[]> =>
        medplum && participant
          ? medplum.searchResourceClass(ExtendedObservation, {
              ...instrumentGroupQuery({
                subject: participant,
                groupIds: [groupId],
              }),
              _count: 100,
            })
          : [],
      enabled: !!participant && !!medplum,
    })),
  })

  // Kept by content, so a refetch that changes nothing changes nothing.
  const entries = useMemoValue<[string, ConditionValue][]>([
    ...targets.references.map((reference, index): [string, ConditionValue] => {
      const query = reads[index]
      const data = query?.data
      if (query && unread(query)) {
        return [reference, 'missing']
      }
      return [reference, data ? observationValueOf(data) : null]
    }),
    ...targets.latest.map(
      ({ system, code }, index): [string, ConditionValue] => {
        const query = latest[index]
        const data = query?.data
        const key = latestValueKey(system, code)
        if (query && unread(query)) {
          return [key, 'missing']
        }
        return [key, captureValue(data ?? [])]
      }
    ),
    ...targets.groups.flatMap((groupId, index): [string, ConditionValue][] => {
      const query = groups[index]
      if (query && unread(query)) {
        return [[groupValueKey(groupId), 'missing']]
      }
      return (query?.data ?? []).flatMap((record) => {
        const coding = record.codeableConcept.coding?.[0]
        return coding?.code
          ? [
              [groupValueKey(groupId, coding), observationValueOf(record)] as [
                string,
                ConditionValue,
              ],
            ]
          : []
      })
    }),
  ])

  useEffect(() => {
    if (!enabled || !editor || editor.isDestroyed) {
      return
    }
    editor.commands.setConditionValues(new Map(entries))
  }, [enabled, editor, entries])
}

// What a note's conditions could read: every observation node's record, or
// latest value for a frozen one (a frozen panel's total among them), when
// the note has any condition at all.
function valueTargets(doc: PMNode): ValueTargets {
  let conditional = false
  const references = new Set<string>()
  const groups = new Set<string>()
  const latest = new Map<string, { system: string; code: string }>()
  doc.descendants((node) => {
    if (node.attrs.showWhen) {
      conditional = true
    }
    if (node.type.name === OBSERVATION_PANEL_NODE) {
      if (node.attrs.groupId) {
        groups.add(node.attrs.groupId)
      }
      const frozenTotal = frozenPanelTotal(node.attrs)
      if (frozenTotal) {
        latest.set(
          latestValueKey(frozenTotal.system, frozenTotal.code),
          frozenTotal
        )
      }
      return false
    }
    if (node.type.name !== OBSERVATION_NODE) {
      return true
    }
    const key = conditionValueKey(node.attrs)
    if (!key) {
      return false
    }
    if (node.attrs.mode === OBSERVATION_MODE_LATEST) {
      latest.set(key, {
        system: String(node.attrs.system ?? ''),
        code: String(node.attrs.code),
      })
    } else {
      references.add(key)
    }
    return false
  })
  if (!conditional) {
    return NO_TARGETS
  }
  return {
    references: [...references].sort(),
    latest: [...latest.values()],
    groups: [...groups].sort(),
  }
}
